import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { ArrowRight, CalendarCheck, CalendarX, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { listPeriods, savePeriod, deletePeriod, getDeleteProgress, setPeriodOnCalendar, previewDeleteLastSessionDates } from "@/lib/schedule.functions";
import { getMe } from "@/lib/core.functions";
import { PageHeader, EmptyState } from "@/components/AppShell";
import { formatRange } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export const Route = createFileRoute("/_authenticated/schedule/")({
  head: () => ({
    meta: [
      { title: "Schedule builder | Leadership Edge Scheduling" },
      { name: "description", content: "Set a timeframe, review deviations, preview the generated schedule, then deploy it." },
      { property: "og:title", content: "Schedule builder | Leadership Edge Scheduling" },
      { property: "og:description", content: "Timeframes, previews, and deployments for course schedules." },
    ],
  }),
  component: SchedulePage,
});

function CalendarToggle({ id, name, onCalendar, built }: { id: string; name: string; onCalendar: boolean; built: boolean }) {
  const toggle = useServerFn(setPeriodOnCalendar);
  const queryClient = useQueryClient();
  const m = useMutation({
    mutationFn: () => toggle({ data: { id, onCalendar: !onCalendar } }),
    onSuccess: () => {
      toast.success(onCalendar ? `${name} removed from the calendar.` : `${name} added to the calendar — final, ready to deploy.`);
      queryClient.invalidateQueries();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  return (
    <Button
      variant={onCalendar ? "ghost" : "soft"}
      size="sm"
      disabled={m.isPending || (!onCalendar && !built)}
      title={!onCalendar && !built ? "Build the schedule before adding it to the calendar" : undefined}
      onClick={() => {
        if (onCalendar && !confirm(`Delete ${name} from the calendar? It will no longer count as final.`)) return;
        m.mutate();
      }}
    >
      {onCalendar ? <CalendarX /> : <CalendarCheck />}
      {onCalendar ? "Delete from Calendar" : "Add to Calendar"}
    </Button>
  );
}

const statusTone: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  previewed: "bg-accent/12 text-accent",
  deployed: "bg-success/12 text-success",
};

function SchedulePage() {
  const queryClient = useQueryClient();
  const fetchMe = useServerFn(getMe);
  const fetchPeriods = useServerFn(listPeriods);
  const save = useServerFn(savePeriod);

  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => fetchMe() });
  const { data: periods, isPending } = useQuery({ queryKey: ["periods"], queryFn: () => fetchPeriods() });

  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [form, setForm] = useState<{ name: string; start_date: string; end_date: string; build_notes: string } | null>(null);

  const saveMutation = useMutation({
    mutationFn: (payload: { name: string; start_date: string; end_date: string; build_notes: string }) =>
      save({ data: { ...payload, build_notes: payload.build_notes.trim() || null } }),
    onSuccess: () => {
      toast.success("Timeframe created.");
      setForm(null);
      queryClient.invalidateQueries({ queryKey: ["periods"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const isAdmin = me?.isAdmin ?? false;

  return (
    <>
      <PageHeader
        eyebrow="Step 1"
        title="Schedule builder"
        description="Pick the window you are scheduling, then open it to collect deviations, preview the build, export, and deploy."
        actions={
          isAdmin ? (
            <Button
              variant="brass"
              onClick={() =>
                setForm({
                  name: "",
                  start_date: new Date().toISOString().slice(0, 10),
                  end_date: new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10),
                  build_notes: "",
                })
              }
            >
              <Plus /> New timeframe
            </Button>
          ) : null
        }
      />

      {isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (periods ?? []).length === 0 ? (
        <EmptyState title="No timeframes yet" body="Create a timeframe such as Q4 2026 to build a schedule for it." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {(periods ?? []).map((period) => (
            <article key={period.id} className="panel flex flex-col p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-display text-xl text-foreground">{period.name}</h2>
                  <p className="mt-1 font-mono text-xs text-muted-foreground">
                    {formatRange(period.start_date, period.end_date)}
                  </p>
                </div>
                <span
                  className={`rounded-full px-2.5 py-1 text-xs font-medium capitalize ${statusTone[period.status] ?? "bg-muted text-muted-foreground"}`}
                >
                  {period.status}
                </span>
              </div>
              <div className="mt-3">
                <span
                  className={`rounded-full px-2.5 py-1 text-xs font-medium ${period.added_to_calendar ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"}`}
                >
                  {period.added_to_calendar ? "Added to Calendar · Final / Ready to deploy" : "Provisional"}
                </span>
              </div>

              <div className="mt-6 flex items-center justify-between gap-2">
                <Button variant="soft" size="sm" asChild>
                  <Link to="/schedule/$periodId" params={{ periodId: period.id }}>
                    Open <ArrowRight />
                  </Link>
                </Button>
                {isAdmin ? (
                  <CalendarToggle id={period.id} name={period.name} onCalendar={!!period.added_to_calendar} built={period.status !== "draft"} />
                ) : null}
                {isAdmin ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:bg-destructive/10"
                    onClick={() => {
                      setDeleteTarget({ id: period.id, name: period.name });
                    }}
                  >
                    <Trash2 />
                  </Button>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      )}

      <DeleteTimeframeDialog target={deleteTarget} onClose={() => setDeleteTarget(null)} />

      <Dialog open={form !== null} onOpenChange={(open) => !open && setForm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New timeframe</DialogTitle>
            <DialogDescription>Name it however you refer to it internally, for example Q4 2026.</DialogDescription>
          </DialogHeader>

          {form ? (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="period-name">Name</Label>
                <Input
                  id="period-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Q4 2026"
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="period-start">Starts</Label>
                  <Input
                    id="period-start"
                    type="date"
                    value={form.start_date}
                    onChange={(e) => {
                      const start = e.target.value;
                      const next = start ? nextDayIso(start) : "";
                      setForm({
                        ...form,
                        start_date: start,
                        end_date:
                          start && (!form.end_date || form.end_date <= start) ? next : form.end_date,
                      });
                    }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="period-end">Ends</Label>
                  <Input
                    id="period-end"
                    type="date"
                    value={form.end_date}
                    min={form.start_date ? nextDayIso(form.start_date) : undefined}
                    onChange={(e) => {
                      const end = e.target.value;
                      if (end && form.start_date && end <= form.start_date) {
                        setForm({ ...form, end_date: nextDayIso(form.start_date) });
                        return;
                      }
                      setForm({ ...form, end_date: end });
                    }}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="period-notes">Notes to consider</Label>
                <Textarea
                  id="period-notes"
                  rows={4}
                  value={form.build_notes}
                  onChange={(e) => setForm({ ...form, build_notes: e.target.value })}
                  placeholder="For example: no classes the week of Dec 22, keep everything between 9:00 and 15:00, skip Fridays, leave Derek out of this quarter."
                />
                <p className="text-xs text-muted-foreground">
                  Written in plain language. The builder reads these notes each time you build or rebuild the preview.
                </p>
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="soft" onClick={() => setForm(null)}>
              Cancel
            </Button>
            <Button
              variant="brass"
              disabled={saveMutation.isPending || !form?.name.trim()}
              onClick={() => form && saveMutation.mutate(form)}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

type DeleteState = Awaited<ReturnType<typeof getDeleteProgress>>;

function DeleteTimeframeDialog({ target, onClose }: { target: { id: string; name: string } | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const remove = useServerFn(deletePeriod);
  const poll = useServerFn(getDeleteProgress);
  const [progressId, setProgressId] = useState<string | null>(null);
  const [state, setState] = useState<DeleteState>(null);
  const [running, setRunning] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [manual, setManual] = useState<Record<string, { date: string; time: string }>>({});
  const previewDates = useServerFn(previewDeleteLastSessionDates);
  const plan = useQuery({
    queryKey: ["delete-date-plan", target?.id],
    queryFn: () => previewDates({ data: { id: target!.id } }),
    enabled: !!target && !progressId,
    staleTime: 0,
  });
  const canConfirm =
    !plan.isLoading && !plan.error &&
    (plan.data ?? []).every((c) => !c.needsManual || /^\d{4}-\d{2}-\d{2}$/.test(manual[c.courseId]?.date ?? ""));

  useEffect(() => {
    if (!target) {
      setProgressId(null);
      setState(null);
      setRunning(false);
      setFailure(null);
      setManual({});
    }
  }, [target]);

  useEffect(() => {
    if (!running || !progressId) return;
    const t = setInterval(() => {
      poll({ data: { progressId } }).then((p) => p && setState(p)).catch(() => {});
    }, 1000);
    return () => clearInterval(t);
  }, [running, progressId, poll]);

  const start = async () => {
    if (!target || running || !canConfirm) return;
    const manualDates = (plan.data ?? [])
      .filter((c) => c.needsManual)
      .map((c) => ({ courseId: c.courseId, date: manual[c.courseId]!.date, time: manual[c.courseId]?.time || null }));
    const id = crypto.randomUUID();
    setFailure(null);
    setRunning(true);
    setState(null);
    let started = false;
    try {
      setProgressId(id);
      started = true;
      const result = await remove({ data: { id: target.id, progressId: id, manualDates } });
      setState(result as DeleteState);
    } catch (e) {
      setFailure((e as Error).message);
    } finally {
      setRunning(false);
      if (!started) setProgressId(null);
      queryClient.invalidateQueries({ queryKey: ["periods"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["courses"] });
    }
  };

  const finished = !running && (state?.phase === "done" || state?.phase === "error" || failure);
  const stepLabel: Record<string, string> = {
    fetching: "Fetching sessions and calendar event details",
    calendar: "Deleting Google Calendar events",
    database: "Deleting sessions from the Scheduler",
    finishing: "Finalizing deletion",
  };

  return (
    <Dialog open={target !== null} onOpenChange={(open) => !open && !running && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => running && e.preventDefault()}>
        {!progressId ? (
          <>
            <DialogHeader>
              <DialogTitle>Delete Timeframe and Sessions?</DialogTitle>
              <DialogDescription asChild>
                <div className="space-y-3 text-sm">
                  <p>
                    This action will permanently delete the selected timeframe and its associated sessions from the
                    Scheduler. It will also attempt to delete the associated Google Calendar events from the trainers'
                    calendars.
                  </p>
                  <p>
                    This action cannot be undone. Sessions already deployed to Training Pipeline will not be deleted from
                    TP and may remain there.
                  </p>
                  <p className="font-medium text-foreground">Are you sure you want to continue?</p>
                </div>
              </DialogDescription>
            </DialogHeader>
            {plan.isLoading ? (
              <p className="text-sm text-muted-foreground">Checking last virtual session dates…</p>
            ) : plan.error ? (
              <p className="text-sm text-destructive">Could not check last virtual session dates: {(plan.error as Error).message}</p>
            ) : plan.data && plan.data.length > 0 ? (
              <div className="space-y-2 text-sm">
                <p className="font-medium text-foreground">Last virtual session dates that will change</p>
                <ul className="max-h-64 space-y-2 overflow-y-auto rounded-md bg-muted p-2">
                  {plan.data.map((c) => (
                    <li key={c.courseId} className="space-y-1">
                      <p>
                        <span className="font-medium">{c.courseName}</span>: {c.currentDate}
                        {c.currentTime ? ` ${c.currentTime}` : ""} →{" "}
                        {c.needsManual ? (
                          <span className="text-warning">no deployed sessions remain — enter a date</span>
                        ) : (
                          `${c.newDate}${c.newTime ? ` ${c.newTime}` : ""}`
                        )}
                      </p>
                      {c.needsManual ? (
                        <div className="flex flex-col gap-2 sm:flex-row">
                          <Input
                            type="date"
                            aria-label={`Last virtual session date for ${c.courseName}`}
                            value={manual[c.courseId]?.date ?? ""}
                            onChange={(e) => setManual((m) => ({ ...m, [c.courseId]: { date: e.target.value, time: m[c.courseId]?.time ?? "" } }))}
                          />
                          <Input
                            type="time"
                            aria-label={`Last virtual session time for ${c.courseName}`}
                            value={manual[c.courseId]?.time ?? ""}
                            onChange={(e) => setManual((m) => ({ ...m, [c.courseId]: { date: m[c.courseId]?.date ?? "", time: e.target.value } }))}
                          />
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground">Dates are saved only if the timeframe is actually deleted. Other courses are not changed.</p>
              </div>
            ) : null}
            {failure ? <p className="text-sm text-destructive">{failure}</p> : null}
            <DialogFooter className="flex-col-reverse gap-2 sm:flex-row">
              <Button variant="soft" onClick={onClose}>Cancel</Button>
              <Button variant="destructive" onClick={start} disabled={!canConfirm}>Confirm Delete</Button>
            </DialogFooter>
          </>
        ) : !finished ? (
          <>
            <DialogHeader>
              <DialogTitle>Deleting {target?.name}…</DialogTitle>
              <DialogDescription>Please keep this window open until deletion finishes.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 text-sm">
              <p className="font-medium text-foreground">{stepLabel[state?.phase ?? "fetching"] ?? "Working…"}</p>
              {state && state.phase !== "fetching" ? (
                <p className="text-muted-foreground">
                  Calendar events: {state.calendar.done} of {state.calendar.total} completed
                </p>
              ) : null}
              {state && state.calendar.total > 0 ? (
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <div className="h-full bg-accent transition-all" style={{ width: `${(state.calendar.done / state.calendar.total) * 100}%` }} />
                </div>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>
                {failure || state?.error
                  ? "Deletion could not finish"
                  : state?.retained
                    ? "Timeframe kept — deletion incomplete"
                    : "Timeframe deleted"}
              </DialogTitle>
              <DialogDescription>{state?.periodName || target?.name}</DialogDescription>
            </DialogHeader>
            {failure ? <p className="text-sm text-destructive">{failure}</p> : null}
            {state ? (
              <div className="space-y-3 text-sm">
                <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1">
                  <dt className="text-muted-foreground">Total sessions processed</dt><dd>{state.sessions}</dd>
                  <dt className="text-muted-foreground">Calendar events deleted</dt><dd>{state.calendar.deleted}</dd>
                  <dt className="text-muted-foreground">Events already missing or deleted</dt><dd>{state.calendar.missing}</dd>
                  <dt className="text-muted-foreground">Missing event IDs (not verified)</dt>
                  <dd className={state.calendar.noEventId ? "text-warning" : ""}>{state.calendar.noEventId}</dd>
                  <dt className="text-muted-foreground">Calendar deletion failures</dt>
                  <dd className={state.calendar.failed ? "text-destructive" : ""}>{state.calendar.failed}</dd>
                  <dt className="text-muted-foreground">Scheduler records</dt>
                  <dd className={state.dbDeleted ? "text-success" : "text-destructive"}>{state.dbDeleted ? "Deleted" : "Kept"}</dd>
                </dl>
                {state.error ? <p className="text-destructive">{state.error}</p> : null}
                {state.retainReason ? (
                  <p className="rounded-md bg-destructive/10 p-2 text-destructive">{state.retainReason}</p>
                ) : null}
                {state.unverified.length > 0 ? (
                  <div className="space-y-1">
                    <p className="text-foreground">
                      These sessions had no saved calendar event ID, so their calendar events could not be verified and
                      may still exist in Google Calendar:
                    </p>
                    <ul className="max-h-36 space-y-1 overflow-y-auto rounded-md bg-muted p-2 text-xs">
                      {state.unverified.map((u, i) => <li key={i}>{u}</li>)}
                    </ul>
                  </div>
                ) : null}
                {state.failures.length > 0 ? (
                  <div className="space-y-1">
                    <p className="text-foreground">
                      These events could not be deleted and are still in the trainers' calendars:
                    </p>
                    <ul className="max-h-48 space-y-1 overflow-y-auto rounded-md bg-muted p-2 text-xs">
                      {state.failures.map((f, i) => (
                        <li key={i}><span className="font-medium">{f.session}</span>: {f.error}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {state.courseDates?.length ? (
                  <div className="space-y-1">
                    <p className="text-foreground">Last virtual session dates updated:</p>
                    <ul className="max-h-36 space-y-1 overflow-y-auto rounded-md bg-muted p-2 text-xs">
                      {state.courseDates.map((c, i) => (
                        <li key={i} className={c.error ? "text-destructive" : ""}>
                          <span className="font-medium">{c.course}</span>: {c.from} → {c.to}
                          {c.manual ? " (entered manually)" : ""}
                          {c.error ? ` — not saved: ${c.error}` : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : null}
            <DialogFooter>
              <Button variant="brass" onClick={onClose}>Close</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function nextDayIso(iso: string): string {
  const [y = 0, m = 1, d = 1] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + 1));
  return dt.toISOString().slice(0, 10);
}
